/**
 * API client for NILE RapidConnect Checkout
 */

import * as Sentry from "@sentry/react";
import type {
  ApiResponse,
  Cart,
  CartItem,
  ShippingOption,
  CreateOrderRequest,
  CreateOrderResponse,
  OrderWithItems,
  OrdersBySessionResponse,
  UpdateCartRequest,
  RemoveFromCartRequest,
  ValidateDiscountRequest,
  ValidateDiscountResponse,
  DiscountDataEntry,
  CustomerGets,
  IneligibleItem,
  CombinesWith,
  CreateDraftOrderRequest,
  CreateDraftOrderResponse,
  PrefilOrderRequest,
  PrefilOrderResponse,
  CheckoutLinkResponse,
  CheckoutSessionCreated,
  CheckoutSessionCreateRequest,
  CheckoutLinkRequest,
  CheckoutRequiresCapResponse,
  CheckoutUpdateCartItem,
  CheckoutUpdateCartResponse,
} from "@/types";
import { config, getApiUrl, getCheckoutApiUrl } from "@/lib/config";
import { getSessionToken } from "@/lib/utils";
import {
  isDevCartEnabled,
  getOrCreateDevCart,
  updateDevCartItem,
  removeDevCartItem,
} from "./dev-cart";

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Make authenticated API request
 * Handles non-JSON responses and network errors
 *
 * `auth: "public"` skips the session token, for endpoints called before a
 * session exists (the express cart). `auth: { token }` sends that token
 * instead of the stored checkout one (express cart sessions are kept apart
 * until the shopper continues to checkout).
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  urlBuilder: (path: string) => string = getApiUrl,
  auth: "session" | "public" | { token: string } = "session",
): Promise<T> {
  const sessionToken =
    auth === "session" ? getSessionToken() : auth === "public" ? null : auth.token;

  if (auth === "session" && !sessionToken) {
    throw new ApiError("No session token found", "NO_SESSION_TOKEN", 401);
  }

  const url = urlBuilder(endpoint);

  let response: Response;
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (sessionToken) {
      headers[config.sessionTokenHeader] = sessionToken;
    }

    // Add Supabase anon key if configured (required for edge functions)
    if (config.supabaseAnonKey) {
      headers["Authorization"] = `Bearer ${config.supabaseAnonKey}`;
    }

    response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });
  } catch (error) {
    const apiError = new ApiError(
      error instanceof Error ? error.message : "Network request failed",
      "NETWORK_ERROR",
      undefined,
      error,
    );
    // Only capture network errors that aren't expected (like offline)
    if (error instanceof TypeError && error.message.includes("fetch")) {
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(apiError, {
          tags: { api_endpoint: endpoint, error_type: "network" },
          extra: { url },
        });
      }
    }
    throw apiError;
  }

  // Handle non-2xx responses
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      const error = new ApiError(
        `API request failed: ${response.statusText}`,
        "NETWORK_ERROR",
        response.status,
      );
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { api_endpoint: endpoint, http_status: response.status },
          extra: { url, statusText: response.statusText },
        });
      }
      throw error;
    }

    if (errorData && !errorData.success) {
      const error = new ApiError(
        errorData.error?.message || "An error occurred",
        errorData.error?.code,
        response.status,
        errorData.error?.details,
      );
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: {
            api_endpoint: endpoint,
            error_code: errorData.error?.code,
            http_status: response.status,
          },
          extra: { url, errorData },
        });
      }
      throw error;
    }

    const error = new ApiError(
      "An unexpected error occurred",
      "UNKNOWN_ERROR",
      response.status,
    );
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: { api_endpoint: endpoint, http_status: response.status },
        extra: { url, errorData },
      });
    }
    throw error;
  }

  let data: ApiResponse<T>;
  try {
    data = await response.json();
  } catch (error) {
    const parseError = new ApiError(
      "Invalid response from server",
      "PARSE_ERROR",
      response.status,
    );
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(parseError, {
        tags: { api_endpoint: endpoint, error_type: "parse" },
        extra: {
          url,
          originalError: error instanceof Error ? error.message : String(error),
        },
      });
    }
    throw parseError;
  }

  if (!data.success) {
    const apiError = new ApiError(
      data.error.message,
      data.error.code,
      response.status,
      data.error.details,
    );
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(apiError, {
        tags: {
          api_endpoint: endpoint,
          error_code: data.error.code,
          http_status: response.status,
        },
        extra: { url, errorData: data.error },
      });
    }
    throw apiError;
  }

  return data.data;
}

/**
 * Make authenticated request to the checkout edge function
 */
async function checkoutApiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  return apiRequest<T>(endpoint, options, getCheckoutApiUrl);
}

/**
 * Transform API cart response to match expected SPA format
 */
function transformCartResponse(cart: Cart): Cart {
  return {
    ...cart,
    items: cart.items.map((item) => {
      const apiItem = item as Cart["items"][0] & { price_cents?: number };
      return {
        ...item,
        unit_price_cents: apiItem.price_cents ?? item.unit_price_cents,
      };
    }),
  };
}

/**
 * Cart API
 */
export const cartApi = {
  /**
   * Get cart contents
   */
  getCart: async (sessionId: string): Promise<Cart> => {
    if (isDevCartEnabled()) {
      return getOrCreateDevCart(sessionId);
    }
    const cart = await apiRequest<Cart>(`/cart/${sessionId}`);
    return transformCartResponse(cart);
  },

  /**
   * Update cart item quantity
   */
  updateItem: async (request: UpdateCartRequest): Promise<Cart> => {
    if (isDevCartEnabled()) {
      return updateDevCartItem(request);
    }
    const cart = await apiRequest<Cart>("/cart/update", {
      method: "PUT",
      body: JSON.stringify(request),
    });
    return transformCartResponse(cart);
  },

  /**
   * Remove item from cart
   */
  removeItem: async (request: RemoveFromCartRequest): Promise<Cart> => {
    if (isDevCartEnabled()) {
      return removeDevCartItem(request);
    }
    const cart = await apiRequest<Cart>("/cart/item", {
      method: "DELETE",
      body: JSON.stringify(request),
    });
    return transformCartResponse(cart);
  },
};

/**
 * Checkout API
 */
export const checkoutApi = {
  /**
   * Get shipping options
   */
  getShippingOptions: async (): Promise<{
    options: ShippingOption[];
    currency: string;
  }> => {
    return checkoutApiRequest<{ options: ShippingOption[]; currency: string }>(
      "/shipping-options",
    );
  },

  /**
   * Create order from cart (single-brand or marketplace)
   */
  createOrder: async (
    request: CreateOrderRequest,
  ): Promise<CreateOrderResponse> => {
    return checkoutApiRequest<CreateOrderResponse>("/create-order", {
      method: "POST",
      body: JSON.stringify(request),
    });
  },

  /**
   * Pre-fill a pending order with shipping info before payment
   */
  prefilOrder: async (
    request: PrefilOrderRequest,
  ): Promise<PrefilOrderResponse> => {
    return checkoutApiRequest<PrefilOrderResponse>("/session-order-prefil", {
      method: "POST",
      body: JSON.stringify(request),
    });
  },
};
export const ordersApi = {
  /**
   * Get order details with items
   */
  getOrder: async (orderId: string): Promise<OrderWithItems> => {
    return apiRequest<OrderWithItems>(`/orders/${orderId}`);
  },

  /**
   * Get orders by session (returns array of orders for the session)
   */
  getOrdersBySession: async (): Promise<OrdersBySessionResponse> => {
    return apiRequest<OrdersBySessionResponse>("/orders");
  },
};

/**
 * Customer lookup response type
 */
export interface CustomerLookupResponse {
  found: boolean;
  customer?: {
    name: string | null;
    email: string | null;
  };
  shipping: {
    name: string;
    email: string | null;
    phone: string;
    address: string;
    address2?: string | null;
    city: string;
    postal_code: string;
    province: string;
  } | null;
}

/**
 * Customers API
 */
export const customersApi = {
  /**
   * Look up customer by phone number for auto-fill
   */
  lookupByPhone: async (
    phoneNumber: string,
    retailerId: string,
  ): Promise<CustomerLookupResponse> => {
    return apiRequest<CustomerLookupResponse>("/customers/lookup", {
      method: "POST",
      body: JSON.stringify({
        phone_number: phoneNumber,
        retailer_id: retailerId,
      }),
    });
  },
};

/**
 * Session response type
 */
export interface SessionResponse {
  session: {
    id: string;
    customer_id: string | null;
    customer?: {
      name?: string | null;
      email?: string | null;
      phone_number?: string | null;
      metadata?: {
        cumi_customer_id?: string | null;
        [key: string]: unknown;
      } | null;
      shipping_details?: {
        first_name?: string | null;
        last_name?: string | null;
        email?: string | null;
        phone?: string | null;
        address?: string | null;
        address2?: string | null;
        suburb?: string | null;
        city?: string | null;
        postal_code?: string | null;
        province?: string | null;
      };
    };
    retailer_id: string;
    status: string;
    created_at: string;
    last_activity_at: string;
    context?: {
      source?: string;
      ax_classifier?: {
        version: number;
        lastRoute: string;
        updatedAt: string;
        lastIntent: string;
        lastUserMessage: string;
        lastCheckoutStep: string;
        lastAssistantMessage: string;
        focusedProductIdCleared: boolean;
      };
      shipping?: string;
      attribution?: {
        linkId?: string;
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
      };
    };
  };
  retailer: {
    id: string;
    name: string;
    logo_url: string;
    logo_size: number;
    logo_dark_mode: string | null;
    primary_color: string;
    secondary_color: string | null;
    accent_color: string | null;
    has_shopify: boolean;
    is_live: boolean;
  } | null;
  cart: {
    item_count: number;
    subtotal_cents: number;
    currency: string;
  };
}

/**
 * Sessions API
 */
export const sessionsApi = {
  /**
   * Get session details including attribution context
   */
  getSession: async (sessionId: string): Promise<SessionResponse> => {
    const response = await apiRequest<SessionResponse>(
      `/sessions/${sessionId}`,
    );
    return response;
  },
};

/**
 * One in-flight request per method, path, body and token: while one is still
 * working, a second attempt at the same request joins it instead of hitting
 * the endpoint again (e.g. a double retry after the CAP check passes).
 */
const inFlightCheckoutRequests = new Map<string, Promise<unknown>>();

function checkoutRequestKey(path: string, options: RequestInit, sessionToken?: string): string {
  return [options.method ?? "GET", path, sessionToken ?? "", typeof options.body === "string" ? options.body : ""].join(" ");
}

/**
 * Requests to the `checkout` edge function's express cart endpoints
 * (nile-ai-mastra: supabase/functions/checkout/checkout-endpoints.md). Their
 * bodies aren't wrapped in { success, data }, and errors are
 * { success: false, error: "<message or code>" }. `allowUnsuccessful` returns
 * a 2xx body even when it says success: false (update-cart's partial results).
 * Singleton: an identical request already in flight is joined, not repeated.
 */
async function checkoutFunctionRequest<T>(
  path: string,
  options: RequestInit = {},
  sessionToken?: string,
  { allowUnsuccessful = false }: { allowUnsuccessful?: boolean } = {},
): Promise<T> {
  const key = checkoutRequestKey(path, options, sessionToken);
  const inFlight = inFlightCheckoutRequests.get(key);
  if (inFlight) return inFlight as Promise<T>;
  const request = performCheckoutFunctionRequest<T>(path, options, sessionToken, allowUnsuccessful);
  inFlightCheckoutRequests.set(key, request);
  try {
    return await request;
  } finally {
    inFlightCheckoutRequests.delete(key);
  }
}

/** The request itself, made only through checkoutFunctionRequest's singleton guard. */
async function performCheckoutFunctionRequest<T>(
  path: string,
  options: RequestInit,
  sessionToken: string | undefined,
  allowUnsuccessful: boolean,
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (sessionToken) headers[config.sessionTokenHeader] = sessionToken;
  if (config.supabaseAnonKey) headers["Authorization"] = `Bearer ${config.supabaseAnonKey}`;

  let response: Response;
  try {
    response = await fetch(getCheckoutApiUrl(path), { ...options, headers: { ...headers, ...options.headers } });
  } catch (error) {
    throw new ApiError(error instanceof Error ? error.message : "Network request failed", "NETWORK_ERROR");
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Handled below as an unexpected response.
  }
  const failed = body && typeof body === "object" && (body as { success?: unknown }).success === false;
  if (!response.ok || (failed && !allowUnsuccessful)) {
    const error = body && typeof body === "object" ? (body as { error?: unknown }).error : undefined;
    const message = typeof error === "string" ? error : `Request failed: ${response.statusText}`;
    throw new ApiError(message, typeof error === "string" ? error : "CHECKOUT_ERROR", response.status, body);
  }
  if (body === null) throw new ApiError("Invalid response from server", "PARSE_ERROR", response.status);
  return body as T;
}

/**
 * True when an ApiError is the checkout function's CAP response: POST /link
 * (load a link from slug or token) and POST /session-create can return 429
 * with { success: false, requiresCap: true, error }. The flag travels in the
 * error's `details` body; the captcha flow will be built on it.
 */
export function isCapRequired(
  error: unknown,
): error is ApiError & { details: CheckoutRequiresCapResponse } {
  return (
    error instanceof ApiError &&
    typeof error.details === "object" &&
    error.details !== null &&
    (error.details as { requiresCap?: unknown }).requiresCap === true
  );
}

/**
 * Express cart (/cart/:slug and the agent's View Cart link, /cart). Calls that
 * need a session pass its token explicitly, so the express cart never reads
 * or overwrites the stored checkout session until the shopper continues.
 */
export const expressCartApi = {
  /**
   * A link's products and/or a session's cart: `{ slug }` for a brand link;
   * with a token, merged with that session's cart (no slug: its source link).
   */
  getLink: async (request: CheckoutLinkRequest, sessionToken?: string): Promise<CheckoutLinkResponse> => {
    const response = await checkoutFunctionRequest<CheckoutLinkResponse>(
      "/link",
      { method: "POST", body: JSON.stringify(request) },
      sessionToken,
    );
    const toRows = (items: CartItem[]) => transformCartResponse({ items } as Cart).items;
    return {
      ...response,
      // Cart rows use the SPA's unit_price_cents; link products pass through.
      linkItems: response.linkItems?.map((item) => ("product_id" in item ? toRows([item])[0]! : item)),
      cartItems: response.cartItems ? toRows(response.cartItems) : undefined,
    };
  },

  /**
   * Creates an anonymous checkout session for the brand, tied to the link it
   * was opened from. Can be refused with 429 requiresCap (see isCapRequired).
   */
  createSession: async (request: CheckoutSessionCreateRequest): Promise<CheckoutSessionCreated> => {
    const response = await checkoutFunctionRequest<{ data: CheckoutSessionCreated }>("/session-create", {
      method: "POST",
      body: JSON.stringify(request),
    });
    return response.data;
  },

  /**
   * The session's brand WhatsApp chat (GET /whatsapp-contact), for the way back
   * to the conversation. Null when the brand has no active WhatsApp number.
   */
  getWhatsAppContact: async (sessionToken: string): Promise<{ whatsappUrl: string } | null> => {
    try {
      return await checkoutFunctionRequest<{ whatsappUrl: string }>("/whatsapp-contact", {}, sessionToken);
    } catch {
      return null;
    }
  },

  /** A Shopify cart URL built from the session's cart (Shopify-checkout brands). */
  getShopifyCheckoutUrl: async (sessionToken: string): Promise<string> => {
    const response = await checkoutFunctionRequest<{ checkoutUrl: string }>(
      "/shopify-checkout",
      {},
      sessionToken,
    );
    return response.checkoutUrl;
  },

  /**
   * Sets absolute quantities for cart rows (0 removes one). Resolves with
   * per-item results even when some failed; `cart` reflects what succeeded.
   */
  updateCart: async (sessionToken: string, items: CheckoutUpdateCartItem[]): Promise<CheckoutUpdateCartResponse> => {
    const response = await checkoutFunctionRequest<CheckoutUpdateCartResponse>(
      "/update-cart",
      { method: "POST", body: JSON.stringify({ items }) },
      sessionToken,
      { allowUnsuccessful: true },
    );
    return { ...response, cart: transformCartResponse(response.cart) };
  },

  /** Session details (brand theme) for a session the express cart holds a token for. */
  getSession: (sessionId: string, sessionToken: string): Promise<SessionResponse> =>
    apiRequest<SessionResponse>(`/sessions/${sessionId}`, {}, getApiUrl, { token: sessionToken }),
};

/**
 * PostHog Analytics API
 */
export interface TrackAnalyticsRequest {
  session_id: string;
  event: string;
  properties?: Record<string, unknown>;
}

export const analyticsApi = {
  /**
   * Track analytics event via PostHog
   * Uses direct Supabase Edge Function URL
   * Returns true if successful, false otherwise 5c901958-9ea9-494f-b10d-c27d9ddad60d
   */
  track: async (request: TrackAnalyticsRequest): Promise<boolean> => {
    const sessionToken = getSessionToken();
    if (!sessionToken) {
      return false;
    }

    const url = import.meta.env.VITE_API_ANALYTICS;

    console.log(sessionToken)

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "x-session-token": `${sessionToken}`
    };

    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(request),
      });
    } catch (error) {
      // Network errors return false
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { api_endpoint: "posthog-analytics", error_type: "network" },
          extra: { url },
        });
      }
      return false;
    }

    // Handle non-2xx responses - return false
    if (!response.ok) {
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        try {
          const errorData = await response.json();
          Sentry.captureException(new Error("Analytics tracking failed"), {
            tags: { api_endpoint: "posthog-analytics", error_type: "http" },
            extra: { url, status: response.status, errorData },
          });
        } catch {
          Sentry.captureException(new Error("Analytics tracking failed"), {
            tags: { api_endpoint: "posthog-analytics", error_type: "http" },
            extra: { url, status: response.status },
          });
        }
      }
      return false;
    }

    // Parse successful response
    try {
      const data = await response.json();
      return data.success === true;
    } catch (error) {
      // If response parsing fails, return false
      return false;
    }
  },
};

/**
 * Discount API
 */
export const discountApi = {
  validate: async (
    request: ValidateDiscountRequest,
  ): Promise<ValidateDiscountResponse> => {
    const baseUrl =
      import.meta.env.VITE_API_HONO_VERIFY_DISCOUNT ??
      "https://whatsappagent.onnile.shop";
    const response = await fetch(`${baseUrl}/discount-verify`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      console.error("API request failed - ", response.statusText);
    }

    const result = await response.json();

    const rawData = result.data?.data ?? result.data;

    // Marketplace: data.data is an array of per-brand discount entries
    if (Array.isArray(rawData)) {
      const entries = rawData.map((entry: Record<string, unknown>) => ({
        brand_id: entry.brand_id as string | undefined,
        valid: (entry.valid as boolean) ?? false,
        discountValue: entry.discountValue as number,
        CodeType: entry.CodeType as "DiscountPercentage" | "DiscountAmount" | undefined,
        DiscountType: (entry.DiscountType as DiscountDataEntry["DiscountType"]) ?? "DiscountCodeBasic",
        customerGets: entry.customerGets as CustomerGets[] | CustomerGets | undefined,
        BxGyVariantLevel: entry.BxGyVariantLevel as boolean | undefined,
        getsVariantLevel: entry.getsVariantLevel as boolean | undefined,
        getsQuantity: entry.getsQuantity as number | undefined,
        ineligible_items: entry.ineligible_items as IneligibleItem[] | undefined,
        combinesWith: entry.combinesWith as CombinesWith | undefined,
        message: entry.message as string | undefined,
      }));
      return {
        data: {
          combinationResultMessage: result.data?.combinationResultMessage ?? "",
          data: entries,
          totalDiscountsSet: result.data?.totalDiscountsSet,
          draftOrders: result.data?.draftOrders,
          newDiscounts: result.data?.newDiscounts,
        }
      };
    }

    // Non-marketplace: single discount entry
    const inner = rawData;

    if (inner.valid == false) {
      return {
        data: {
          combinationResultMessage: result.data?.combinationResultMessage ?? "",
          data: {
            message: inner.message,
            DiscountType: "DiscountCodeBasic",
            discountValue: undefined as unknown as number,
            valid: false,
          },
        }
      };
    }

    return {
      data: {
        combinationResultMessage: result.data?.combinationResultMessage ?? "",
        data: {
          DiscountType: inner.DiscountType,
          discountValue: inner.discountValue,
          valid: true,
          CodeType: inner.CodeType,
          customerGets: inner.customerGets,
          BxGyVariantLevel: inner.BxGyVariantLevel,
          getsVariantLevel: inner.getsVariantLevel,
          getsQuantity: inner.getsQuantity,
          ineligible_items: inner.ineligible_items,
          combinesWith: inner.combinesWith,
          newDiscounts: result.data?.newDiscounts,
        },
        totalDiscountsSet: result.data?.totalDiscountsSet,
      }
    };
  },
  // validate: async (request: ValidateDiscountRequest): Promise<ValidateDiscountResponse> => {
  // return apiRequest<ValidateDiscountResponse>("/financial/discount-code-verify", {
  //   method: "POST",
  //   body: JSON.stringify(request),
  // });
  // }
};

/**
 * Draft Order API
 */
export const draftApi = {
  DraftOrder: async (
    request: CreateDraftOrderRequest,
    method: "POST" | "PUT" = "POST",
  ): Promise<CreateDraftOrderResponse> => {
    const baseUrl =
      import.meta.env.VITE_API_HONO_VERIFY_DISCOUNT ??
      "https://whatsappagent.onnile.shop";
    const response = await fetch(`${baseUrl}/draft-new-order`, {
      method,
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      console.error("[client.ts - DraftOrder] -", response.statusText);
      throw new ApiError(
        "Draft order creation failed",
        "DRAFT_ORDER_ERROR",
        response.status,
      );
    }

    const result = await response.json();
    return result.data ?? result;
  },
};

/**
 * Paystack initialization response type
 */
export interface InitializePaystackTransactionResponse {
  access_code: string,
  message: string,
  reference: string,
  status: boolean
}

/**
 * Paystack API
 */
export const paystackApi = {
  /**
   * Initialize a Paystack transaction on the server
   * Returns an access code for resumeTransaction flow
   */
  initialize: async (
    metadata: any,
  ): Promise<InitializePaystackTransactionResponse> => {
    return checkoutApiRequest<InitializePaystackTransactionResponse>(
      "/initialize-payment",
      {
        method: "POST",
        body: JSON.stringify({ metadata: metadata }),
      },
    );
  },
};
